'use client';

import { useDisclosure } from '@nextui-org/react';
import type { UseDisclosureReturn } from '@nextui-org/use-disclosure';
import React, { createContext, useContext, useMemo, useRef, useState } from 'react';

import { useSQuery, ValidateFieldError } from '@/utils';

type PromptProviderProps = {
  children: React.ReactNode;
};

export type PromptProviderState = {
  file?: File;
  processId?: number | string;
  generateType?: string;
  model?: string;
  assetId?: number;
  prompt: string;
  progress: string;
  done: boolean;
  url: string;
  step: number;
  imageId: number | string;
  readyRun: boolean;
  customId:
    | 'V1'
    | 'V2'
    | 'V3'
    | 'V4'
    | 'U1'
    | 'U2'
    | 'U3'
    | 'U4'
    | 'x15'
    | 'x20'
    | '4x'
    | '2x'
    | 'strong'
    | null;
  tool?: string;
  usedOptions?: string[];
  transferred?: boolean;
  imageConfig?: {
    batch_size: number;
    cfg_scale: number;
    enable_hires: number;
    height: number;
    lora: string;
    model: string;
    negative_prompt: string;
    samplers: string;
    seed: string;
    steps: number;
    width: number;
  };
  upScale?: boolean;
};

type PromptProviderContextType = {
  state: PromptProviderState[];
  setState: React.Dispatch<React.SetStateAction<PromptProviderState[]>>;
  isMintNow: boolean;
  setIsMintNow: React.Dispatch<React.SetStateAction<boolean>>;
  listSold: number[];
  setIsSold: React.Dispatch<React.SetStateAction<number[]>>;
  openCampaign: boolean;
  setOpenCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  modelConfigRetry: any;
  useModalValidate: UseDisclosureReturn;
  validateFieldError: ValidateFieldError;
};

export const initialState: PromptProviderState = {
  assetId: 0,
  prompt: '',
  progress: '0%',
  done: false,
  url: '',
  step: 1,
  imageId: 0,
  readyRun: false,
  customId: null,
};

const PromptProviderContext = createContext<PromptProviderContextType>({
  state: [initialState],
  setState: () => {},
  isMintNow: true,
  setIsMintNow: () => {},
  listSold: [],
  setIsSold: () => {},
  openCampaign: false,
  setOpenCampaign: () => {},
  modelConfigRetry: null,
  // @ts-ignore
  useModalValidate: null,
  validateFieldError: new ValidateFieldError(),
});

export const PromptProvider: React.FC<PromptProviderProps> = ({ children }) => {
  const [state, setState] = useState<PromptProviderState[]>([initialState]);
  const [isMintNow, setIsMintNow] = useState<boolean>(true);
  const [listSold, setIsSold] = useState<number[]>([]);
  const [openCampaign, setOpenCampaign] = useState<boolean>(false);
  const validateFieldError = useRef(new ValidateFieldError());
  const useModalValidate = useDisclosure({
    onClose() {
      validateFieldError.current.cleanErrors();
    },
  });

  const { data: modelConfigRetry } = useSQuery({
    queryKey: ['model-config-retry'],
  });

  const contextValue: PromptProviderContextType = useMemo(() => {
    return {
      state,
      setState,
      isMintNow,
      setIsMintNow,
      listSold,
      setIsSold,
      openCampaign,
      setOpenCampaign,
      modelConfigRetry,
      useModalValidate,
      validateFieldError: validateFieldError.current,
    };
  }, [
    state,
    setState,
    isMintNow,
    setIsMintNow,
    listSold,
    setIsSold,
    openCampaign,
    setOpenCampaign,
    modelConfigRetry,
    useModalValidate,
    validateFieldError,
  ]);

  return (
    <PromptProviderContext.Provider value={contextValue}>{children}</PromptProviderContext.Provider>
  );
};

export const usePrompt = () => useContext(PromptProviderContext);
